import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";
import {
    splitFormAtom,
    SplitFormAtomAction,
} from "@impero-com/common/utils/form/splitFormAtom";
import { assertNever } from "@impero-com/common/utils/obj";
import { atom, WritableAtom } from "jotai";
import * as Yup from "yup";

export type InsertableSplitFormAtom<Item> = WritableAtom<
    FormAtom<Item>[],
    [action: SplitFormAtomAction<Item>],
    FormAtom<Item>
> & {
    /**
     * The item validation schema
     */
    schema: Yup.ISchema<Item>;
};

/**
 * Transform a form atom holding an array into an atom holding an array of form atoms,
 * one for each array entry, like splitFormAtom.
 * Returns the affected form atom on set.
 *
 * Triggers a revalidation of the affected form atom on insert & move.
 *
 * @param arrayAtom the atom to split
 * @param keyExtractor item's key,
 * needed for performant and well functioning array reordering and finding back the correct affected atoms.
 * The item atoms schema is derived from the array schema.
 */
export function insertableSplitFormAtom<Item, Key>(
    arrayAtom: FormAtom<Item[]>,
    keyExtractor: (item: Item) => Key
): InsertableSplitFormAtom<Item> {
    const splitted = splitFormAtom(arrayAtom, keyExtractor);

    return {
        ...atom(
            (get) => get(splitted),
            (get, set, action) => {
                void set(splitted, action);
                switch (action.type) {
                    case "insert": {
                        const key = keyExtractor(action.value);
                        const formAtom = get(splitted).find(
                            (formAtom) => keyExtractor(get(formAtom)) === key
                        )!;
                        void set(formAtom.validateFieldAtom, true);
                        return formAtom;
                    }
                    case "move": {
                        void set(action.atom.validateFieldAtom, true);
                        return action.atom;
                    }
                    case "remove":
                    case "reset": {
                        return action.atom;
                    }
                    default: {
                        assertNever(action);
                    }
                }
            }
        ),
        schema: splitted.schema,
    };
}
